import { useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Card,
  Chip,
  Input,
  ListBox,
  Select,
  Spinner,
  Switch,
  TextArea,
  TextField,
} from '@heroui/react';
import { ArrowLeft, Download, FileText, KeyRound, RefreshCw, Trash2, Upload } from 'lucide-react';
import { useClientEditPage, type ClientEditMode } from './useClientEditPage';
import { DOCUMENT_KIND_LABELS, LEGAL_FORM_LABELS } from '@/lib/types';
import { FieldLabel } from '@/components/molecules/FieldLabel';
import { ColorPicker } from '@/components/molecules/ColorPicker';
import { formatDateTime } from '@/lib/formatDate';
import { PageContainer } from '@/components/templates/PageContainer';

const LEGAL_FORM_OPTIONS = (
  Object.keys(LEGAL_FORM_LABELS) as (keyof typeof LEGAL_FORM_LABELS)[]
).map((id) => ({ id, label: LEGAL_FORM_LABELS[id] }));

/** Заголовок карточки по шкале типографики HeroUI: h5 — «Card titles» (18px/600). */
function CardHeading({ children }: { children: React.ReactNode }) {
  return <Card.Title className="text-lg font-semibold">{children}</Card.Title>;
}

/** Read-only строка (последний вход логина). */
function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-medium text-foreground">{label}</span>
      <p className="rounded-[var(--radius)] bg-surface-secondary px-3 py-2 text-sm text-muted">
        {value}
      </p>
    </div>
  );
}

export function ClientEditPage({ mode = 'admin' }: { mode?: ClientEditMode }) {
  const isSelf = mode === 'self';
  const {
    clientId,
    client,
    documents,
    fieldCatalog,
    form,
    login,
    loginForm,
    isLoading,
    notFound,
    isSaving,
    isToggling,
    isSavingLogin,
    setField,
    setLoginField,
    handleSave,
    handleToggleEnabled,
    handleCreateLogin,
    handleUpdateLogin,
    handleUploadLogo,
    handleUploadDocument,
    handleDeleteDocument,
  } = useClientEditPage(mode);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const offerInputRef = useRef<HTMLInputElement>(null);
  const consentInputRef = useRef<HTMLInputElement>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-sm text-muted">
        <Spinner size="sm" />
        Загрузка…
      </div>
    );
  }

  if (notFound) {
    return <p className="py-20 text-center text-sm text-muted">Клиент не найден</p>;
  }

  /** Трёхпозиционный статус поля: Скрыто / Не обязательно / Обязательно. */
  const setWorkerFieldStatus = (slug: string, status: string) => {
    const next = { ...form.worker_fields };
    if (status === 'required' || status === 'optional') next[slug] = status;
    else delete next[slug];
    setField('worker_fields', next);
  };

  return (
    <PageContainer className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {/* self-режим — свой кабинет клиента: назад на дашборд */}
          <Link
            to={isSelf ? '/dashboard' : '/clients'}
            className="flex shrink-0 items-center gap-1 text-sm text-muted hover:text-foreground"
          >
            <ArrowLeft className="size-4" />
            {isSelf ? 'Дашборд' : 'Клиенты'}
          </Link>
          <h2 className="truncate text-2xl font-bold text-foreground">
            {isSelf
              ? (client?.title ?? 'Данные организации')
              : clientId
                ? (client?.title ?? 'Клиент')
                : 'Новый клиент'}
          </h2>
          {!isSelf && client && (
            <Chip color={client.enabled ? 'success' : 'danger'} size="sm" variant="soft">
              {client.enabled ? 'Активен' : 'Неактивен'}
            </Chip>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button onPress={() => void handleSave()} isDisabled={isSaving}>
            {isSaving ? <Spinner size="sm" /> : 'Сохранить'}
          </Button>
        </div>
      </div>

      {/* Как на странице визита: слева основной контент, справа — боковая карточка */}
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[1fr_320px]">
        <div className="flex min-w-0 flex-col gap-4">
          <Card className="bg-surface">
            <Card.Header className="flex-row items-center justify-between">
              <CardHeading>Организация</CardHeading>
              {/* Активностью управляет админ; клиент своих карточку не деактивирует */}
              {!isSelf && client && (
                <Switch
                  isSelected={client.enabled}
                  isDisabled={isToggling}
                  onChange={(selected) => void handleToggleEnabled(selected)}
                >
                  <Switch.Content>
                    <Switch.Control>
                      <Switch.Thumb />
                    </Switch.Control>
                    Активна
                  </Switch.Content>
                </Switch>
              )}
            </Card.Header>
            <Card.Content className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <TextField
                  variant="secondary"
                  value={form.title}
                  onChange={(v) => setField('title', v)}
                  isRequired={!isSelf}
                >
                  <FieldLabel>Название организации</FieldLabel>
                  <Input placeholder="ООО «Ромашка»" />
                </TextField>

                <Select
                  className="w-full"
                  variant="secondary"
                  value={form.legal_form}
                  onChange={(key) => setField('legal_form', String(key ?? 'ooo'))}
                >
                  <FieldLabel>Правовая форма</FieldLabel>
                  <Select.Trigger>
                    <Select.Value />
                    <Select.Indicator />
                  </Select.Trigger>
                  <Select.Popover>
                    <ListBox>
                      {LEGAL_FORM_OPTIONS.map((opt) => (
                        <ListBox.Item key={opt.id} id={opt.id} textValue={opt.label}>
                          {opt.label}
                          <ListBox.ItemIndicator />
                        </ListBox.Item>
                      ))}
                    </ListBox>
                  </Select.Popover>
                </Select>

                <TextField
                  variant="secondary"
                  value={form.phone}
                  onChange={(v) => setField('phone', v)}
                >
                  <FieldLabel>Телефон</FieldLabel>
                  <Input placeholder="+7 900 000-00-00" />
                </TextField>
                {/* Email организации — ПД клиента (clients.email), доступен без логина */}
                <TextField
                  variant="secondary"
                  value={form.email}
                  onChange={(v) => setField('email', v)}
                >
                  <FieldLabel>Email</FieldLabel>
                  <Input type="email" placeholder="client@example.com" />
                </TextField>
                <TextField
                  variant="secondary"
                  value={form.general_director}
                  onChange={(v) => setField('general_director', v)}
                >
                  <FieldLabel>Генеральный директор</FieldLabel>
                  <Input placeholder="Иванов И.И." />
                </TextField>
              </div>

              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-foreground">Адрес</span>
                <TextArea
                  variant="secondary"
                  aria-label="Адрес"
                  className="min-h-16"
                  value={form.address}
                  onChange={(e) => setField('address', e.target.value)}
                  placeholder="Юридический адрес организации"
                />
              </div>
            </Card.Content>
          </Card>

          <Card className="bg-surface">
            <Card.Header>
              <CardHeading>Оформление</CardHeading>
            </Card.Header>
            {/* Grid с items-start: подписи «Логотип» и «Цвет» на одном уровне */}
            <Card.Content className="grid grid-cols-[auto_auto] items-start justify-start gap-x-8 gap-y-2">
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleUploadLogo(file);
                  e.target.value = '';
                }}
              />
              {/* Логотип: крупный пунктирный квадрат, клик — загрузка */}
              <button
                type="button"
                aria-label={
                  client
                    ? client.logoUrl
                      ? 'Заменить логотип'
                      : 'Загрузить логотип'
                    : 'Логотип будет доступен после сохранения клиента'
                }
                title={client ? undefined : 'Сохраните клиента, чтобы загрузить логотип'}
                onClick={() => logoInputRef.current?.click()}
                disabled={!client}
                className="flex size-32 items-center justify-center overflow-hidden rounded-[var(--radius)] border border-dashed border-[var(--border)] bg-surface-secondary transition-colors hover:border-accent disabled:cursor-not-allowed disabled:opacity-60"
              >
                {client?.logoUrl ? (
                  <img
                    src={client.logoUrl}
                    alt={client.title}
                    className="size-full object-contain"
                  />
                ) : (
                  <Upload className="size-8 text-muted" />
                )}
              </button>

              {/* Цвет: компактное поле «свотч + hex», как в редакторе шаблона */}
              <ColorPicker
                variant="field"
                value={form.primary_color}
                onChange={(v) => setField('primary_color', v)}
              />

              <span className="col-start-1 justify-self-center text-xs text-muted">Логотип</span>
            </Card.Content>
          </Card>

          <Card className="bg-surface">
            <Card.Header>
              <CardHeading>Данные сотрудника</CardHeading>
            </Card.Header>
            <Card.Content>
              <div className="flex flex-col gap-3">
                {fieldCatalog.map((field) => {
                  const status = form.worker_fields[field.slug] ?? 'hidden';
                  return (
                    <div
                      key={field.slug}
                      className="flex items-center justify-between gap-3 rounded-[var(--radius)] bg-surface-secondary px-3 py-2"
                    >
                      <span className="text-sm text-foreground">{field.label}</span>
                      <Select
                        className="w-48"
                        variant="secondary"
                        aria-label={`Статус поля ${field.label}`}
                        value={status}
                        onChange={(key) =>
                          setWorkerFieldStatus(field.slug, String(key ?? 'hidden'))
                        }
                      >
                        <Select.Trigger>
                          <Select.Value />
                          <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                          <ListBox>
                            <ListBox.Item id="hidden" textValue="Скрыто">
                              Скрыто
                              <ListBox.ItemIndicator />
                            </ListBox.Item>
                            <ListBox.Item id="optional" textValue="Не обязательно">
                              Не обязательно
                              <ListBox.ItemIndicator />
                            </ListBox.Item>
                            <ListBox.Item id="required" textValue="Обязательно">
                              Обязательно
                              <ListBox.ItemIndicator />
                            </ListBox.Item>
                          </ListBox>
                        </Select.Popover>
                      </Select>
                    </div>
                  );
                })}
              </div>
              <p className="mt-3 text-xs text-muted">
                «Обязательно» — поле обязательно к заполнению исполнителем в приложении; выбранные
                данные редактируются в карточке сотрудника
              </p>
            </Card.Content>
          </Card>
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          {/* Логин клиента — админская карточка (пароль/активность);
              клиент свой логин не управляет */}
          {!isSelf && client && (
            <Card className="bg-surface">
              <Card.Header>
                <CardHeading>Логин</CardHeading>
              </Card.Header>
              <Card.Content className="flex flex-col gap-4">
                {login ? (
                  <ReadOnlyField label="Логин" value={login.login} />
                ) : (
                  <ReadOnlyField label="Логин" value="Не создан" />
                )}
                <TextField
                  variant="secondary"
                  value={loginForm.password}
                  onChange={(v) => setLoginField('password', v)}
                  isRequired={!login}
                >
                  <FieldLabel>{login ? 'Новый пароль' : 'Пароль'}</FieldLabel>
                  <Input type="password" placeholder="••••••••" autoComplete="new-password" />
                </TextField>
                {login ? (
                  <>
                    <Switch
                      isSelected={loginForm.enabled}
                      isDisabled={isSavingLogin}
                      onChange={(selected) => setLoginField('enabled', selected)}
                    >
                      <Switch.Content>
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                        Активен
                      </Switch.Content>
                    </Switch>
                    <ReadOnlyField
                      label="Последний вход"
                      value={formatDateTime(login.lastLoginAt)}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="self-start"
                      onPress={() => void handleUpdateLogin()}
                      isDisabled={isSavingLogin}
                    >
                      {isSavingLogin ? <Spinner size="sm" /> : <RefreshCw className="size-4" />}
                      Обновить
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="self-start"
                    onPress={() => void handleCreateLogin()}
                    isDisabled={isSavingLogin}
                  >
                    {isSavingLogin ? <Spinner size="sm" /> : <KeyRound className="size-4" />}
                    Создать логин
                  </Button>
                )}
              </Card.Content>
            </Card>
          )}

          <Card className="bg-surface">
            <Card.Header>
              <CardHeading>Реквизиты</CardHeading>
            </Card.Header>
            <Card.Content className="flex flex-col gap-4">
              <TextField variant="secondary" value={form.inn} onChange={(v) => setField('inn', v)}>
                <FieldLabel>ИНН</FieldLabel>
                <Input />
              </TextField>
              <TextField variant="secondary" value={form.kpp} onChange={(v) => setField('kpp', v)}>
                <FieldLabel>КПП</FieldLabel>
                <Input />
              </TextField>
              <TextField variant="secondary" value={form.bik} onChange={(v) => setField('bik', v)}>
                <FieldLabel>БИК</FieldLabel>
                <Input />
              </TextField>
              <TextField
                variant="secondary"
                value={form.checking_account}
                onChange={(v) => setField('checking_account', v)}
              >
                <FieldLabel>Расчетный счет</FieldLabel>
                <Input />
              </TextField>
              <TextField
                variant="secondary"
                value={form.correspondent_account}
                onChange={(v) => setField('correspondent_account', v)}
              >
                <FieldLabel>Корреспондентский счет</FieldLabel>
                <Input />
              </TextField>
            </Card.Content>
          </Card>

          {client && (
            <Card className="bg-surface">
              <Card.Header>
                <CardHeading>Документы (PDF)</CardHeading>
              </Card.Header>
              <Card.Content className="flex flex-col gap-2">
                {documents.length === 0 && (
                  <p className="text-xs text-muted">Документы не загружены</p>
                )}
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex flex-col gap-1.5 rounded-[var(--radius)] bg-surface-secondary px-3 py-2"
                  >
                    <a
                      href={doc.url}
                      download
                      className="flex min-w-0 items-center gap-1.5 text-sm text-link hover:underline"
                      title="Скачать"
                    >
                      <FileText className="size-4 shrink-0" />
                      <span className="truncate">{doc.title ?? doc.url}</span>
                      <Download className="size-3.5 shrink-0" />
                    </a>
                    <div className="flex items-center justify-between gap-2">
                      <Chip size="sm" variant="soft">
                        {DOCUMENT_KIND_LABELS[doc.kind]}
                      </Chip>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Удалить документ"
                        onPress={() => void handleDeleteDocument(doc.id)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                <input
                  ref={offerInputRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleUploadDocument(file, 'offer');
                    e.target.value = '';
                  }}
                />
                <input
                  ref={consentInputRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleUploadDocument(file, 'pd_consent');
                    e.target.value = '';
                  }}
                />
                <Button
                  variant="outline"
                  className="w-full"
                  onPress={() => offerInputRef.current?.click()}
                >
                  <Upload className="size-4" />
                  Загрузить оферту
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onPress={() => consentInputRef.current?.click()}
                >
                  <Upload className="size-4" />
                  Загрузить согласие на ПД
                </Button>
              </Card.Content>
            </Card>
          )}
        </div>
      </div>
    </PageContainer>
  );
}
